import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Maximize2, Minimize2, Mic } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../hooks/useLanguage';

// Strip leading [Outil ...] blocks (balanced [...]; nested ] inside JSON breaks naive regex)
const cleanToolBlocks = (text) => {
  let t = text;
  while (true) {
    const s = t.trimStart();
    if (!/^\[\s*Outil/i.test(s)) break;
    let depth = 0;
    let cut = -1;
    for (let i = 0; i < s.length; i += 1) {
      if (s[i] === '[') depth += 1;
      else if (s[i] === ']') {
        depth -= 1;
        if (depth === 0) {
          cut = i;
          break;
        }
      }
    }
    if (cut < 0) break;
    t = s.slice(cut + 1);
  }
  return t.trim();
};

/** Right-side drawer layout. Opt out with REACT_APP_CHAT_WIDGET_DRAWER=false */
function isChatDrawerLayoutEnabled() {
  if (process.env.REACT_APP_CHAT_WIDGET_DRAWER === 'false') return false;
  if (process.env.REACT_APP_CHAT_WIDGET_DRAWER === 'true') return true;
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'dev.wownautic.com' ||
    h === 'wownautic.com' ||
    h === 'www.wownautic.com' ||
    h === 'localhost' ||
    h === '127.0.0.1'
  );
}

// Markdown-aware message renderer
const MessageContent = ({ content, isUser }) => {
  if (isUser) return content;
  const cleaned = cleanToolBlocks(content);
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children }) => (
          <table className="border-collapse w-full my-2 text-xs">{children}</table>
        ),
        thead: ({ children }) => (
          <thead className="bg-gray-100">{children}</thead>
        ),
        th: ({ children }) => (
          <th className="border border-gray-300 px-2 py-1 text-left font-semibold">{children}</th>
        ),
        td: ({ children }) => (
          <td className="border border-gray-300 px-2 py-1">{children}</td>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>
        ),
        li: ({ children }) => <li className="leading-snug">{children}</li>,
        p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">{children}</a>
        ),
      }}
    >
      {cleaned}
    </ReactMarkdown>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOTEMIUM_CONV_STORAGE_KEY = 'totemium_conversation_id';

const ChatWidget = () => {
  const { t, language } = useLanguage();
  const drawerLayout = isChatDrawerLayoutEnabled();
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [agentInfo, setAgentInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [isDictating, setIsDictating] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchAgentInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/totemium/agent-info`);
        if (res.data?.configured) {
          setAgentInfo(res.data);
        }
      } catch (err) {
        // silently fail — widget won't show
      }
    };
    fetchAgentInfo();
  }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(TOTEMIUM_CONV_STORAGE_KEY);
      if (raw == null || raw === '') return;
      const n = Number(raw);
      setConversationId(Number.isFinite(n) && String(n) === raw ? n : raw);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const hasUserMessage = messages.some(m => m.role === 'user');
    if (!hasUserMessage && messages.length > 0) {
      setMessages([{ role: 'assistant', content: t('chatWidget.welcomeMessage') }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (!drawerLayout || !isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerLayout, isOpen]);

  useEffect(() => {
    if (!drawerLayout || !isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeChat();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerLayout, isOpen]);

  useEffect(() => () => {
    const r = recognitionRef.current;
    recognitionRef.current = null;
    if (r) {
      try {
        r.stop();
      } catch (_) {
        try {
          r.abort();
        } catch (__) {
          /* ignore */
        }
      }
    }
  }, []);

  const stopDictation = () => {
    const r = recognitionRef.current;
    recognitionRef.current = null;
    if (r) {
      try {
        r.stop();
      } catch (_) {
        try {
          r.abort();
        } catch (__) {
          /* ignore */
        }
      }
    }
    setIsDictating(false);
  };

  const toggleDictation = () => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) return;
    if (recognitionRef.current) {
      stopDictation();
      return;
    }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US';
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (!event.results[i].isFinal) continue;
        const transcript = event.results[i][0].transcript.trim();
        if (!transcript) continue;
        setInput((prev) => {
          const base = prev.trimEnd();
          return base ? `${base} ${transcript}` : transcript;
        });
      }
    };
    recognition.onerror = () => {
      recognitionRef.current = null;
      setIsDictating(false);
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setIsDictating(false);
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsDictating(true);
    } catch (_) {
      recognitionRef.current = null;
      setIsDictating(false);
    }
  };

  const sendMessage = async () => {
    stopDictation();
    const question = input.trim();
    if (!question || isTyping) return;

    const userMsg = { role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    try {
      const body = { question };
      if (conversationId != null && conversationId !== '') {
        body.conversation_id = conversationId;
      }
      const res = await axios.post(`${API_URL}/api/totemium/chat`, body);

      const data = res.data;
      const botMsg = {
        role: 'assistant',
        content: data.answer || data.response || t('chatWidget.errorMessage')
      };
      setMessages(prev => [...prev, botMsg]);

      const nextCid = data.conversation_id;
      if (nextCid != null && nextCid !== '') {
        setConversationId(nextCid);
        try {
          sessionStorage.setItem(TOTEMIUM_CONV_STORAGE_KEY, String(nextCid));
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      const botMsg = {
        role: 'assistant',
        content: t('chatWidget.errorMessage'),
        error: true
      };
      setMessages(prev => [...prev, botMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openChat = () => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: t('chatWidget.welcomeMessage')
      }]);
    }
    setIsOpen(true);
    requestAnimationFrame(() => setIsAnimating(true));
  };

  const closeChat = () => {
    stopDictation();
    setIsAnimating(false);
    setIsFullscreen(false);
    setTimeout(() => setIsOpen(false), 250);
  };

  const toggleChat = () => {
    if (isOpen) closeChat();
    else openChat();
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  const startNewConversation = () => {
    stopDictation();
    setConversationId(null);
    try {
      sessionStorage.removeItem(TOTEMIUM_CONV_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setMessages([{ role: 'assistant', content: t('chatWidget.welcomeMessage') }]);
  };

  if (!isVisible || !agentInfo) return null;

  const agentName = agentInfo.agent_name || 'Assistant';
  const agentIcon = agentInfo.agent_icon;
  const dictationSupported =
    typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const bubbleMax = drawerLayout ? 'max-w-[min(92%,36rem)]' : 'max-w-[75%]';
  const msgTextSize = drawerLayout ? 'text-sm' : 'text-sm';

  const header = (
    <div className="bg-gradient-to-r from-[#0B1F3F] to-[#1a3a6b] px-4 py-3 flex items-center gap-3 flex-shrink-0 border-b border-white/10">
      {agentIcon ? (
        <img src={agentIcon} alt={agentName} className="w-10 h-10 rounded-full object-cover border-2 border-white/30" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-white" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm truncate">{agentName}</p>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-white/70 text-xs">{t('chatWidget.online')}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={startNewConversation}
        className="text-white/70 hover:text-white transition-colors px-2 py-1 text-xs font-medium rounded-md hover:bg-white/10"
        title={t('chatWidget.newConversationTitle')}
      >
        {t('chatWidget.newConversation')}
      </button>
      <button
        type="button"
        onClick={toggleFullscreen}
        className="text-white/70 hover:text-white transition-colors p-1"
        title={isFullscreen ? 'Minimize' : 'Fullscreen'}
      >
        {isFullscreen ? (
          <Minimize2 className="w-5 h-5" />
        ) : (
          <Maximize2 className="w-5 h-5" />
        )}
      </button>
      <button
        type="button"
        onClick={closeChat}
        className="text-white/70 hover:text-white transition-colors p-1"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );

  const messageList = (
    <div className={`flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-gray-50 ${drawerLayout ? 'pb-2' : ''}`}>
      {messages.map((msg, idx) => (
        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          {msg.role === 'assistant' && (
            <div className="flex-shrink-0 mr-2 mt-1">
              {agentIcon ? (
                <img src={agentIcon} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-[#0B1F3F] flex items-center justify-center">
                  <MessageCircle className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </div>
          )}
          <div
            className={`${bubbleMax} px-3.5 py-2.5 rounded-2xl ${msgTextSize} leading-relaxed ${
              msg.role === 'user'
                ? 'bg-[#0B1F3F] text-white rounded-br-md'
                : msg.error
                  ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                  : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
            }`}
            style={{ wordBreak: 'break-word' }}
          >
            <MessageContent content={msg.content} isUser={msg.role === 'user'} />
          </div>
        </div>
      ))}

      {isTyping && (
        <div className="flex justify-start">
          <div className="flex-shrink-0 mr-2 mt-1">
            {agentIcon ? (
              <img src={agentIcon} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-[#0B1F3F] flex items-center justify-center">
                <MessageCircle className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
          <div className="bg-white text-gray-500 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );

  const inputBar = (
    <div className="border-t border-gray-200 px-3 py-2.5 bg-white flex items-center gap-2 flex-shrink-0">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('chatWidget.placeholder')}
        disabled={isTyping}
        className="flex-1 min-w-0 text-sm border-0 outline-none bg-gray-100 rounded-full px-4 py-2.5 text-gray-800 placeholder:text-gray-400 disabled:opacity-50"
      />
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          type="button"
          onClick={toggleDictation}
          disabled={isTyping || !dictationSupported}
          title={
            !dictationSupported
              ? t('chatWidget.dictationNotSupported')
              : isDictating
                ? t('chatWidget.dictationStop')
                : t('chatWidget.dictationStart')
          }
          aria-pressed={isDictating}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors border ${
            isDictating
              ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
              : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:text-[#0B1F3F]'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <Mic className={`w-4 h-4 ${isDictating ? 'animate-pulse' : ''}`} />
        </button>
        <button
          type="button"
          onClick={sendMessage}
          disabled={!input.trim() || isTyping}
          className="w-9 h-9 rounded-full bg-[#0B1F3F] text-white flex items-center justify-center hover:bg-[#1a3a6b] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isTyping ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );

  const footer = (
    <div className="text-center py-1.5 bg-gray-50 border-t border-gray-100 flex-shrink-0">
      <span className="text-[10px] text-gray-400">
        Powered by{' '}
        <a href="https://totemium.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700">
          Totemium
        </a>
      </span>
    </div>
  );

  return (
    <>
      {isOpen && drawerLayout && (
        <>
          <button
            type="button"
            className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ease-out ${
              isAnimating ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={closeChat}
            aria-label="Close chat"
          />
          <div
            className={`fixed z-50 top-0 right-0 h-full flex flex-col bg-white shadow-2xl border-l border-gray-200 transition-transform duration-300 ease-out ${
              isFullscreen ? 'w-full max-w-none' : 'w-full max-w-md sm:max-w-xl'
            } ${isAnimating ? 'translate-x-0' : 'translate-x-full'}`}
            role="dialog"
            aria-modal="true"
            aria-label={t('chatWidget.chatWithUs')}
          >
            {header}
            {messageList}
            {inputBar}
            {footer}
          </div>
        </>
      )}

      {isOpen && !drawerLayout && (
        <div
          className={`fixed z-50 bg-white flex flex-col overflow-hidden transition-all duration-300 ease-out ${
            isFullscreen
              ? 'inset-0 rounded-none'
              : 'bottom-24 md:bottom-20 right-4 md:right-8 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl border border-gray-200'
          } ${
            isAnimating
              ? 'opacity-100 translate-y-0 scale-100'
              : 'opacity-0 translate-y-4 scale-95'
          }`}
          style={isFullscreen ? {} : { height: '500px', maxHeight: 'calc(100vh - 160px)' }}
        >
          {header}
          {messageList}
          {inputBar}
          {footer}
        </div>
      )}

      {!(drawerLayout && isOpen) && (
        <button
          type="button"
          onClick={toggleChat}
          className="fixed bottom-24 md:bottom-8 right-4 md:right-8 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-xl hover:scale-110 transition-transform duration-300 flex items-center justify-center animate-bounce-subtle group"
          aria-label={t('chatWidget.ariaLabel')}
        >
          {isOpen ? (
            <X className="w-8 h-8" />
          ) : (
            <MessageCircle className="w-8 h-8 fill-current" />
          )}
          {!isOpen && (
            <span className="absolute right-full mr-4 bg-white text-gray-800 px-4 py-2 rounded-lg shadow-lg text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden md:block">
              {t('chatWidget.chatWithUs')}
            </span>
          )}
        </button>
      )}
    </>
  );
};

export default ChatWidget;
